import type { ReactElement } from "react";
import type { TimelineEvent } from "../../../experience-shell/workflows/timeline";
import type { TimelineFilterWorkItem } from "../WorkflowTimelineFilters";

export type TimelineSubjectKind = "workflow" | "queue";

export interface WorkflowTimelineProps {
  readonly events: readonly TimelineEvent[];
  /** No feed on hand yet. Renders the skeleton. Never true during a refetch. */
  readonly isLoading: boolean;
  /** `events` belong to the previous subject while the next feed loads. Dims the feed. */
  readonly isStale?: boolean;
  readonly error?: Error | null;
  /** True when the BFF capped the merged feed; surfaced as an inline note. */
  readonly truncated: boolean;
  /** True when at least one upstream ledger stream failed. */
  readonly partial: boolean;
  readonly onBack?: () => void;
  /** Re-run the failed load. When present the error state renders a retry action. */
  readonly onRetry?: () => void;
  readonly workflowTitle?: string;
  /**
   * Identifies the audited subject when this shared ledger renderer is used
   * outside a workflow. Omitted callers retain the workflow presentation.
   */
  readonly subject?: {
    readonly kind: TimelineSubjectKind;
    readonly title?: string;
  };
  /**
   * Detail-view selector, rendered at the trailing edge of the header so the
   * Board/Timeline control stays in the same place across both views.
   */
  readonly viewSwitcher?: ReactElement;
  /**
   * Render as the body of a scene that owns the header. The composition
   * drops its own header; the filter toggle (below `expanded`) moves to a
   * slim toolbar row. `onBack`, `workflowTitle`, and `viewSwitcher` are
   * ignored in this mode.
   */
  readonly embedded?: boolean;
  /**
   * Optional filter controls + state. The `events` passed in are expected to
   * be already filtered; `workItems` / `activityTypes` derive from the
   * unfiltered feed so the controls stay stable while a filter is active.
   */
  readonly filters?: TimelineFilterControls;
}

/**
 * Presentational contract for the timeline filters. The orchestrator owns
 * the state (mirrored into the URL) and the apply-to-feed logic.
 */
export interface TimelineFilterControls {
  readonly workItems: readonly TimelineFilterWorkItem[];
  readonly selectedWorkItemId: string | null;
  readonly onSelectWorkItem: (id: string | null) => void;
  readonly activityTypes: readonly string[];
  readonly hiddenActivityTypes: ReadonlySet<string>;
  readonly onToggleActivityType: (type: string) => void;
  readonly onResetActivityTypes: () => void;
  readonly onClearAll: () => void;
}

export type RowTone = "neutral" | "created" | "completed" | "removed" | "warning" | "info";

/** Number of narrowing choices the user has applied. */
export function activeFilterCount(filters: TimelineFilterControls | undefined): number {
  if (filters === undefined) return 0;
  return (filters.selectedWorkItemId !== null ? 1 : 0) + filters.hiddenActivityTypes.size;
}
